// Export-format builders. Each turns an AnalysisResult into a string in
// the requested format. Markdown is the comprehensive long-form export
// (intended to be the canonical "share this analysis" format); CSV /
// dimensions CSV / summary are tighter formats for paste into Slack /
// downstream tooling.

import type { AnalysisResult } from './types';
import { formatScore, formatPct } from './format';

function fmtDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
    });
  } catch {
    return iso;
  }
}

function csvEscape(v: unknown): string {
  if (v == null) return '';
  const s = String(v);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function csvRow(...cells: unknown[]): string {
  return cells.map(csvEscape).join(',');
}

// ─────────────────────────── Markdown (comprehensive) ───────────────────────────

export function toMarkdown(r: AnalysisResult): string {
  const out: string[] = [];

  // Header
  out.push(`# ${r.title}`);
  out.push('');
  if (r.headline) out.push(`> ${r.headline}`);
  out.push('');
  out.push(`**Verdict:** ${r.verdict} · **Score:** ${formatScore(r.score)} · **Confidence:** ${formatPct(r.confidence)}`);
  out.push('');
  out.push(`*Document type: ${r.documentType} · Analysed ${fmtDate(r.date)}*`);
  out.push('');
  if (r.summary) {
    out.push('## Summary');
    out.push('');
    out.push(r.summary);
    out.push('');
  }

  // KPIs
  out.push('## Key Metrics');
  out.push('');
  out.push('| Metric | Value | Qualifier |');
  out.push('|---|---|---|');
  out.push(`| Confidence | ${r.kpi.confidence}% | — |`);
  out.push(`| Red Flags | ${r.kpi.redFlags} | — |`);
  out.push(`| Entities Detected | ${r.kpi.entities} | — |`);
  out.push(`| Sections Parsed | ${r.kpi.sections} | — |`);
  out.push('');

  // Bull / bear signals
  if (r.bullSignals.length > 0) {
    out.push('## Bull Signals');
    out.push('');
    for (const s of r.bullSignals) {
      out.push(`- ${s.text}${s.paragraphRef ? ` *(${s.paragraphRef})*` : ''}`);
    }
    out.push('');
  }
  if (r.bearSignals.length > 0) {
    out.push('## Bear Signals');
    out.push('');
    for (const s of r.bearSignals) {
      out.push(`- ${s.text}${s.paragraphRef ? ` *(${s.paragraphRef})*` : ''}`);
    }
    out.push('');
  }

  // Briefing
  if (r.briefing.overall) {
    out.push('## Analytical Briefing');
    out.push('');
    out.push(r.briefing.overall);
    out.push('');
    if (r.briefing.dimensionBreakdown.length > 0) {
      out.push('### Dimension Breakdown');
      out.push('');
      for (const d of r.briefing.dimensionBreakdown) {
        out.push(`**${d.label}.** ${d.copy}`);
        out.push('');
      }
    }
    if (r.briefing.entityLandscape) { out.push('### Entity Landscape'); out.push(''); out.push(r.briefing.entityLandscape); out.push(''); }
    if (r.briefing.trajectory) { out.push('### Document Trajectory'); out.push(''); out.push(r.briefing.trajectory); out.push(''); }
    if (r.briefing.linguisticProfile) { out.push('### Linguistic Profile'); out.push(''); out.push(r.briefing.linguisticProfile); out.push(''); }
  }

  // Dimensions
  if (r.dimensions.length > 0) {
    out.push('## Sentiment Dimensions');
    out.push('');
    out.push('| Dimension | Value | Qualifier |');
    out.push('|---|---|---|');
    for (const d of r.dimensions) {
      out.push(`| ${d.label} | ${d.value.toFixed(2)} | ${d.qualifier} |`);
    }
    out.push('');
  }

  // Speakers
  if (r.speakers && r.speakers.length > 0) {
    out.push('## Speaker Attribution');
    out.push('');
    out.push('| Speaker | Role | Score | Hedge | Forward Modals | Qualifier |');
    out.push('|---|---|---|---|---|---|');
    for (const s of r.speakers) {
      out.push(`| ${s.name} | ${s.role} | ${formatScore(s.score)} | ${(s.hedgeDensity * 100).toFixed(0)}% | ${(s.forwardModalDensity * 100).toFixed(0)}% | ${s.qualifier} |`);
    }
    out.push('');
  }

  // Guidance bridge
  if (r.guidanceBridge && r.guidanceBridge.items.length > 0) {
    out.push('## Guidance Bridge');
    out.push('');
    out.push(`**Net verdict:** ${r.guidanceBridge.netVerdict.toUpperCase()}`);
    out.push('');
    if (r.guidanceBridge.summary) { out.push(r.guidanceBridge.summary); out.push(''); }
    out.push('| Metric | Range | Consensus | Δ | Framing |');
    out.push('|---|---|---|---|---|');
    for (const g of r.guidanceBridge.items) {
      const range = `${g.rangeLo.toFixed(1)}–${g.rangeHi.toFixed(1)}${g.unit}`;
      const cons = g.consensusMid != null ? `${g.consensusMid.toFixed(1)}${g.unit}` : '—';
      const delta = g.delta != null ? `${g.delta > 0 ? '+' : ''}${g.delta.toFixed(2)}${g.unit}` : '—';
      out.push(`| ${g.metric} | ${range} | ${cons} | ${delta} | ${g.framing} |`);
    }
    out.push('');
  }

  // Entities
  if (r.entities.length > 0) {
    out.push('## Entity Sentiment');
    out.push('');
    out.push('| Entity | Type | Sentiment | Score | Context |');
    out.push('|---|---|---|---|---|');
    for (const e of r.entities) {
      out.push(`| ${e.name} | ${e.type} | ${e.verdict} | ${formatScore(e.score)} | ${e.context.replace(/\|/g, '/')} |`);
    }
    out.push('');
  }

  // Section trajectory
  if (r.sections.length > 0) {
    out.push('## Section-by-Section Trajectory');
    out.push('');
    for (const s of r.sections) {
      out.push(`### ${s.idx}. ${s.title} *(${s.range})* — ${s.verdict} ${formatScore(s.score)}`);
      out.push('');
      if (s.tags.length > 0) out.push(`*Tags: ${s.tags.join(', ')}*`);
      out.push('');
      out.push(s.summary);
      out.push('');
    }
  }

  // Q&A
  if (r.qna.length > 0) {
    out.push('## Q&A Deep Dive');
    out.push('');
    for (const q of r.qna) {
      out.push(`### ${q.analyst}${q.firm ? ` — ${q.firm}` : ''}${q.analystKind ? ` *(${q.analystKind})*` : ''}`);
      out.push('');
      out.push(`**Q.** ${q.question}`);
      out.push('');
      out.push(`**A.${q.respondedBy ? ` ${q.respondedBy}:` : ''}** ${q.answerSummary}`);
      out.push('');
      if (q.insight) { out.push(`> *${q.insight}*`); out.push(''); }
      out.push(`**Verdict:** ${q.verdict}${q.flags.length > 0 ? ` · **Flags:** ${q.flags.join(', ')}` : ''}`);
      out.push('');
      if (q.followUps && q.followUps.length > 0) {
        for (const fu of q.followUps) {
          out.push(`**Follow-up (${fu.pressLevel}):** ${fu.question}`);
          out.push('');
          out.push(`**Reply:** ${fu.answerSummary}`);
          out.push('');
        }
      }
    }
  }

  // Numerical
  if (r.numerical.length > 0) {
    out.push('## Numerical Extraction');
    out.push('');
    out.push('| Metric | Value | Period | Direction | Consensus | Beat | Aligned | Framing |');
    out.push('|---|---|---|---|---|---|---|---|');
    for (const n of r.numerical) {
      const cons = n.consensus ? `${n.consensus.value} (${n.consensus.source})` : '—';
      const beat = n.beat ? n.beat.toUpperCase() : '—';
      const aligned = n.aligned ? '✓' : '—';
      const tag = n.nonGaap ? ' (non-GAAP)' : '';
      out.push(`| ${n.metric}${tag} | ${n.value} | ${n.period} | ${n.direction} | ${cons} | ${beat} | ${aligned} | ${n.framing.replace(/\|/g, '/')} |`);
    }
    out.push('');
  }

  // Linguistic indicators
  if (r.linguisticIndicators.length > 0) {
    out.push('## Linguistic Indicators');
    out.push('');
    out.push('| Indicator | Value | Normalized |');
    out.push('|---|---|---|');
    for (const l of r.linguisticIndicators) {
      out.push(`| ${l.label} | ${l.value} | ${(l.rawValue * 100).toFixed(0)}% |`);
    }
    out.push('');
  }

  if (r.fingerprint.length > 0) {
    out.push('## Linguistic Fingerprint');
    out.push('');
    out.push('| Metric | Score |');
    out.push('|---|---|');
    for (const f of r.fingerprint) {
      out.push(`| ${f.label} | ${(f.value * 100).toFixed(0)}% |`);
    }
    out.push('');
  }

  // Sector lexicon
  if (r.sectorLexicon.length > 0) {
    out.push('## Sector-Specific Lexicon');
    out.push('');
    out.push('| Sector | Matches | Positive | Negative | Risk | Score |');
    out.push('|---|---|---|---|---|---|');
    for (const s of r.sectorLexicon) {
      out.push(`| ${s.name} | ${s.matches} | ${s.pos} | ${s.neg} | ${s.risk} | ${formatScore(s.score)} |`);
    }
    out.push('');
  }

  // Tail risk
  if (r.tailRisk) {
    out.push('## Tail Risk');
    out.push('');
    out.push(`**Magnitude:** ${(r.tailRisk.overallScore * 100).toFixed(0)} (${r.tailRisk.qualifier}) · **Weighted exposure:** ${formatScore(r.tailRisk.weightedExposure)}`);
    out.push('');
    if (r.tailRisk.topQuotes.length > 0) {
      out.push('### Notable Hedges');
      out.push('');
      for (const q of r.tailRisk.topQuotes) {
        out.push(`> "${q.quote}" — *${q.term}, ${q.ref}*`);
        out.push('');
      }
    }
  }

  // Risk quotes
  if (r.riskQuotes.length > 0) {
    out.push('## Risk Quotes');
    out.push('');
    for (const q of r.riskQuotes) {
      out.push(`> "${q.quote}" — *${q.category}, ${q.ref}*`);
      out.push('');
    }
  }

  // Verification
  if (r.verification.length > 0) {
    out.push('## Verification');
    out.push('');
    out.push('| Check | Status | Note |');
    out.push('|---|---|---|');
    for (const v of r.verification) {
      out.push(`| ${v.label} | ${v.status} | ${v.note ?? '—'} |`);
    }
    out.push('');
  }

  // Advanced / run details
  if (r.advanced.length > 0) {
    out.push('## Run Details');
    out.push('');
    for (const a of r.advanced) {
      out.push(`- **${a.key}:** ${a.value}`);
    }
    out.push('');
  }

  out.push('---');
  out.push('');
  out.push(`*Generated by [TheInvestmentAnalyst Sentiment Analysis](https://tia-sentiment-dash.netlify.app/result/${r.slug}). Permalink: /result/${r.slug}*`);
  out.push('');

  return out.join('\n');
}

// ─────────────────────────── Summary (paste-into-Slack format) ───────────────────────────

export function toSummary(r: AnalysisResult): string {
  const lines: string[] = [];
  lines.push(`${r.title} — ${r.verdict} ${formatScore(r.score)} · ${formatPct(r.confidence)} confidence`);
  if (r.headline) { lines.push(''); lines.push(r.headline); }
  if (r.summary) { lines.push(''); lines.push(r.summary); }
  if (r.bullSignals.length > 0) {
    lines.push('');
    lines.push('Bull signals:');
    for (const s of r.bullSignals) lines.push(`  • ${s.text}`);
  }
  if (r.bearSignals.length > 0) {
    lines.push('');
    lines.push('Bear signals:');
    for (const s of r.bearSignals) lines.push(`  • ${s.text}`);
  }
  return lines.join('\n');
}

// ─────────────────────────── CSV: numerical extraction ───────────────────────────

export function toNumericalCsv(r: AnalysisResult): string {
  const rows: string[] = [];
  rows.push(csvRow('metric', 'value', 'period', 'direction', 'aligned', 'nonGaap', 'consensus', 'consensusSource', 'beat', 'framing'));
  for (const n of r.numerical) {
    rows.push(csvRow(
      n.metric,
      n.value,
      n.period,
      n.direction,
      n.aligned ? 'true' : 'false',
      n.nonGaap ? 'true' : 'false',
      n.consensus?.value ?? '',
      n.consensus?.source ?? '',
      n.beat ?? '',
      n.framing,
    ));
  }
  return rows.join('\n') + '\n';
}

// ─────────────────────────── CSV: dimensions ───────────────────────────

export function toDimensionsCsv(r: AnalysisResult): string {
  const rows: string[] = [];
  rows.push(csvRow('dimension', 'value', 'qualifier'));
  for (const d of r.dimensions) {
    rows.push(csvRow(d.label, d.value.toFixed(3), d.qualifier));
  }
  return rows.join('\n') + '\n';
}
